const TRACKING_PARAM_KEYS = ["gclid", "wbraid", "gbraid", "_gl"];
const TRACKING_STORAGE_KEY = "book_discovery_tracking_params";

function normalizeTrackingValue(value) {
    const cleaned = String(value || "").trim();
    return cleaned ? cleaned : "";
}

function extractHashQuery(hashValue) {
    const hash = String(hashValue || "");
    const idx = hash.indexOf("?");
    if (idx === -1 || idx === hash.length - 1) return "";
    return hash.slice(idx + 1);
}

function parseTrackingParamsFromSearchParams(searchParams) {
    const out = {};
    for (const key of TRACKING_PARAM_KEYS) {
        const value = normalizeTrackingValue(searchParams.get(key));
        if (value) out[key] = value;
    }
    return out;
}

function mergeTrackingParams(base, extra) {
    const next = { ...(base || {}) };
    for (const key of TRACKING_PARAM_KEYS) {
        const value = normalizeTrackingValue(extra?.[key]);
        if (value) next[key] = value;
    }
    return next;
}

export function isDevelopmentEnvironment() {
    return process.env.NODE_ENV !== "production";
}

export function readTrackingParamsFromLocation(locationLike) {
    const fromSearch = parseTrackingParamsFromSearchParams(
        new URLSearchParams(String(locationLike?.search || ""))
    );
    const fromHash = parseTrackingParamsFromSearchParams(
        new URLSearchParams(extractHashQuery(locationLike?.hash))
    );
    return mergeTrackingParams(fromSearch, fromHash);
}

export function readTrackingParamsFromStorage(storageLike) {
    try {
        const raw = storageLike?.getItem?.(TRACKING_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return mergeTrackingParams({}, parsed);
    } catch {
        return {};
    }
}

export function persistTrackingParams(storageLike, trackingParams) {
    const normalized = mergeTrackingParams({}, trackingParams);
    try {
        if (Object.keys(normalized).length === 0) return {};
        storageLike?.setItem?.(TRACKING_STORAGE_KEY, JSON.stringify(normalized));
        return normalized;
    } catch {
        return normalized;
    }
}

export function captureTrackingParamsForBooking({
    locationLike,
    storageLike,
    log,
} = {}) {
    const fromUrl = readTrackingParamsFromLocation(locationLike);
    const fromStorage = readTrackingParamsFromStorage(storageLike);
    const merged = mergeTrackingParams(fromStorage, fromUrl);
    persistTrackingParams(storageLike, merged);

    if (typeof log === "function" && Object.keys(fromUrl).length > 0) {
        log("[BookDiscovery] Captured tracking params", merged);
    }
    return merged;
}

export function buildBookingRedirectUrl({
    baseUrl,
    includeParams,
    email,
    name,
    trackingParams,
    log,
} = {}) {
    if (!baseUrl) return null;
    try {
        const url = new URL(baseUrl);
        if (includeParams) {
            const cleanEmail = normalizeTrackingValue(email);
            const compactName = normalizeTrackingValue(name).replace(/\s+/g, "");
            if (cleanEmail) url.searchParams.set("email", cleanEmail);
            if (compactName) url.searchParams.set("name", compactName);
        }

        const tracking = mergeTrackingParams({}, trackingParams);
        for (const key of TRACKING_PARAM_KEYS) {
            const value = tracking[key];
            if (!value) continue;
            if (!url.searchParams.has(key)) {
                url.searchParams.set(key, value);
            }
        }

        if (typeof log === "function") {
            log("[BookDiscovery] Redirect target built", url.toString());
        }
        return url.toString();
    } catch {
        return null;
    }
}

export const BOOKING_TRACKING_PARAM_KEYS = TRACKING_PARAM_KEYS;
